import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import css from './VoiceSearch.module.css';
import classNames from "classnames";
import IconCollection from "../../IconCollection/IconCollection";
import { useIntl } from '../../../util/reactIntl';
import { LanguageContext } from '../../../context/LanguageContext';

// BCP-47 tags for the Web Speech API - keep in sync with SUPPORTED_LANGUAGES.
const SPEECH_RECOGNITION_LOCALES = {
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
  zh: 'zh-CN',
};

// How long to wait after the last recognized word before auto-stopping and
// finalizing the search — mirrors the "listens, then acts on a pause"
// behaviour of Google-style voice search instead of requiring an explicit tap.
const SILENCE_TIMEOUT_MS = 2000;
// Hard ceiling on how long we'll wait for the very first result. Some engines
// (older iOS Safari, or a browser whose speech backend can't reach its
// network service) never fire onresult/onerror/onend at all — without this,
// the button would stay stuck in "Listening…" forever with no feedback.
const STARTUP_TIMEOUT_MS = 8000;
// Guards against a tight onend->start->onend loop on engines that end the
// session instantly and repeatedly without ever producing a result.
const MAX_SILENT_RESTARTS = 5;
// How long a transient error message (e.g. "didn't catch that") stays visible
// before clearing itself, so it doesn't linger indefinitely.
const ERROR_DISMISS_MS = 5000;

const getSpeechRecognitionCtor = () =>
  typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

function VoiceSearch({ onVoiceSearch, onListeningChange, className, onActivateOverride }) {
  const intl = useIntl();
  const { locale } = useContext(LanguageContext);

  const SpeechRecognitionCtor = getSpeechRecognitionCtor();
  const isSupported = !!SpeechRecognitionCtor;
  const isSecureContext = typeof window === 'undefined' || window.isSecureContext !== false;

  const [isListening, setIsListening] = useState(false);
  const [errorKey, setErrorKey] = useState(null);

  // Keep the latest callback props in refs so the (long-lived) recognition
  // instance's native event handlers never close over a stale render.
  const onVoiceSearchRef = useRef(onVoiceSearch);
  const onListeningChangeRef = useRef(onListeningChange);
  useEffect(() => {
    onVoiceSearchRef.current = onVoiceSearch;
    onListeningChangeRef.current = onListeningChange;
  });

  const recognitionRef = useRef(null);
  const isListeningRef = useRef(false);
  const stoppedByUserRef = useRef(false);
  const hadErrorRef = useRef(false);
  const hasResultRef = useRef(false);
  const restartAttemptsRef = useRef(0);
  const silenceTimerRef = useRef(null);
  const startupTimerRef = useRef(null);
  const errorTimerRef = useRef(null);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearStartupTimer = useCallback(() => {
    if (startupTimerRef.current) {
      clearTimeout(startupTimerRef.current);
      startupTimerRef.current = null;
    }
  }, []);

  const clearErrorTimer = useCallback(() => {
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
  }, []);

  const showError = useCallback((key, { persist = false } = {}) => {
    clearErrorTimer();
    setErrorKey(key);
    if (!persist) {
      errorTimerRef.current = setTimeout(() => setErrorKey(null), ERROR_DISMISS_MS);
    }
  }, [clearErrorTimer]);

  const applyListening = useCallback(value => {
    isListeningRef.current = value;
    setIsListening(value);
    onListeningChangeRef.current?.(value);
  }, []);

  // Lazily create a single recognition instance and reuse it for the life of
  // the component; only torn down when the locale changes or on unmount.
  const getRecognition = useCallback(() => {
    if (recognitionRef.current) {
      return recognitionRef.current;
    }
    if (!SpeechRecognitionCtor) {
      return null;
    }

    let recognition;
    try {
      recognition = new SpeechRecognitionCtor();
    } catch (e) {
      return null;
    }

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = SPEECH_RECOGNITION_LOCALES[locale] || 'en-US';

    recognition.onresult = event => {
      hasResultRef.current = true;
      clearStartupTimer();
      clearSilenceTimer();
      const transcript = Array.from(event.results)
        .map(result => result[0]?.transcript || '')
        .join('');
      onVoiceSearchRef.current?.(transcript);

      // No new words for a couple of seconds — treat that as the end of the
      // query and stop listening, same as Google's voice search auto-finalize.
      silenceTimerRef.current = setTimeout(() => {
        stoppedByUserRef.current = true;
        recognition.stop();
      }, SILENCE_TIMEOUT_MS);
    };

    recognition.onerror = event => {
      hadErrorRef.current = true;
      stoppedByUserRef.current = true;
      clearSilenceTimer();
      clearStartupTimer();
      switch (event.error) {
        case 'not-allowed':
        case 'service-not-allowed':
          showError('permissionDenied');
          break;
        case 'no-speech':
          showError('noSpeech');
          break;
        case 'network':
          showError('networkError');
          break;
        case 'audio-capture':
          showError('audioCaptureError');
          break;
        case 'aborted':
          // Deliberate stop (ours or the browser's) — not a user-facing error.
          break;
        default:
          showError('genericError');
          console.error('VoiceSearch: unrecognized recognition error', event.error);
      }
      // Don't wait for onend to settle the UI — some browsers never fire it
      // after a permission denial, which would otherwise leave the button
      // stuck showing "listening" indefinitely.
      applyListening(false);
    };

    recognition.onend = () => {
      clearSilenceTimer();
      // Auto-restart only after a clean pause, while the user hasn't asked us
      // to stop and nothing errored. This keeps long dictation going on
      // browsers (notably iOS Safari) that end recognition after every short
      // silence, without looping forever after a real error/permission denial.
      const canRestart = isListeningRef.current && !stoppedByUserRef.current && !hadErrorRef.current;
      if (canRestart && !hasResultRef.current) {
        // Never heard anything yet — cap how many times we'll silently
        // restart so a browser that ends instantly and repeatedly can't
        // spin forever; the startup timer is the final backstop either way.
        restartAttemptsRef.current += 1;
        if (restartAttemptsRef.current > MAX_SILENT_RESTARTS) {
          clearStartupTimer();
          stoppedByUserRef.current = false;
          hadErrorRef.current = false;
          showError('noSpeech');
          applyListening(false);
          return;
        }
      }
      if (canRestart) {
        try {
          recognition.start();
          return;
        } catch (e) {
          // Fall through and settle into the stopped state below.
        }
      }
      clearStartupTimer();
      stoppedByUserRef.current = false;
      hadErrorRef.current = false;
      applyListening(false);
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [SpeechRecognitionCtor, locale, clearSilenceTimer, clearStartupTimer, showError, applyListening]);

  const teardownRecognition = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      return;
    }
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    try {
      recognition.stop();
    } catch (e) {
      // Nothing to stop if it was never started.
    }
    recognitionRef.current = null;
  }, []);

  // Drop a stale instance when the recognition language needs to change.
  // Never tear down mid-session; the next start() will lazily rebuild it.
  useEffect(() => {
    if (!isListeningRef.current) {
      teardownRecognition();
    }
  }, [locale, teardownRecognition]);

  useEffect(() => {
    return () => {
      clearSilenceTimer();
      clearStartupTimer();
      clearErrorTimer();
      stoppedByUserRef.current = true;
      teardownRecognition();
    };
  }, []);

  const start = useCallback(() => {
    setErrorKey(null);
    clearErrorTimer();

    if (!isSecureContext) {
      showError('insecureContext', { persist: true });
      return;
    }

    const recognition = getRecognition();
    if (!recognition) {
      showError('notSupported', { persist: true });
      return;
    }

    stoppedByUserRef.current = false;
    hadErrorRef.current = false;
    hasResultRef.current = false;
    restartAttemptsRef.current = 0;
    applyListening(true);

    clearStartupTimer();
    startupTimerRef.current = setTimeout(() => {
      // Nothing heard at all within the ceiling — the engine may be unable
      // to reach its speech backend. Stop cleanly instead of leaving the UI
      // stuck on "Listening…" indefinitely.
      stoppedByUserRef.current = true;
      showError('noSpeech');
      try {
        recognition.stop();
      } catch (e) {
        applyListening(false);
      }
    }, STARTUP_TIMEOUT_MS);

    try {
      recognition.start();
    } catch (e) {
      // Already-started instances throw InvalidStateError; safe to ignore.
    }
  }, [isSecureContext, getRecognition, showError, clearErrorTimer, clearStartupTimer, applyListening]);

  const stop = useCallback(() => {
    clearSilenceTimer();
    clearStartupTimer();
    stoppedByUserRef.current = true;
    const recognition = recognitionRef.current;
    if (!recognition) {
      applyListening(false);
      return;
    }
    try {
      recognition.stop();
    } catch (e) {
      applyListening(false);
    }
  }, [clearSilenceTimer, clearStartupTimer, applyListening]);

  const handleButtonClick = () => {
    // Lets a host (e.g. the landing page hero, which is just a gateway to
    // the real search page) hijack the tap instead of starting recognition
    // in place.
    if (typeof onActivateOverride === 'function') {
      onActivateOverride();
      return;
    }
    if (isListening) {
      stop();
    } else {
      start();
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape' && isListening) {
      stop();
    }
  };

  const errorMessage = errorKey ? intl.formatMessage({ id: `VoiceSearch.${errorKey}` }) : null;
  const statusMessage = isListening
    ? intl.formatMessage({ id: 'VoiceSearch.listening' })
    : errorMessage || '';

  return (
    <div className={classNames(className, css.container)}>
      <button
        className={css.btn}
        type="button"
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        aria-pressed={isListening}
        aria-label={isListening
          ? intl.formatMessage({ id: 'VoiceSearch.stop' })
          : intl.formatMessage({ id: 'VoiceSearch.start' })}
        title={!isSupported ? intl.formatMessage({ id: 'VoiceSearch.notSupported' }) : undefined}
      >
        {isListening ? (
          <div className={css.listeningContainer}>
            <div className={css.speechButton}>
              <div className={css.pulseRing}></div>
              <svg fill="#000000" width="24px" height="24px" viewBox="-0.105 0 0.72 0.72" xmlns="http://www.w3.org/2000/svg"><path d="M0.252 0.504a0.144 0.144 0 0 0 0.144 -0.144V0.144a0.144 0.144 0 1 0 -0.288 0V0.36a0.144 0.144 0 0 0 0.144 0.144" /><path d="M0.504 0.36V0.288a0.036 0.036 0 1 0 -0.072 0V0.36a0.18 0.18 0 1 1 -0.36 0V0.288a0.036 0.036 0 1 0 -0.072 0V0.36a0.252 0.252 0 0 0 0.215 0.249l0.001 0V0.648H0.108a0.036 0.036 0 1 0 0 0.072h0.288a0.036 0.036 0 1 0 0 -0.072H0.288v-0.039c0.123 -0.018 0.216 -0.123 0.216 -0.249z" /></svg>
            </div>
          </div>
        ) : (
          <div className={css.listeningContainer}>
            <div className={classNames(css.speechButton, css.speechButtonBefore)}>
              <span className={css.speechMicBefore}>
                <IconCollection name="speechMic" />
              </span>
            </div>
          </div>
        )}
      </button>

      {errorMessage ? (
        <span className={css.errorMessage} role="status">
          {errorMessage}
        </span>
      ) : null}

      {/* Accessible live region for announcing listening/error status to screen readers */}
      <div aria-live="polite" aria-atomic="true" className={css.visuallyHidden}>
        {statusMessage}
      </div>
    </div>
  );
}

export default VoiceSearch;
